import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, message, Upload, Empty, InputNumber, DatePicker, Checkbox, Tag, Row, Col, Tabs, Card, List, Radio, Collapse } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { PlusOutlined, FileAddOutlined, ReloadOutlined, WarningOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api/http';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';
import { openWithAuth, buildMinioProxyUrl } from '../utils/authedResource';
import AuthedImage from '../components/AuthedImage';

// 映射说明：后端主表字段 plate_number/owner_name 与前端表单使用的 license_plate/employee_name 需在提交时转换。
// 后续扩展：文档有效期预警、评估次数、违章次数统计等。

// 与后端 ENUM 同步（包含历史值兼容：汽车/自行车）
const vehicleTypeOptions = [
  '小汽车','面包车','货车','摩托车','叉车','电动车','汽车','自行车','其他'
];

const toDayjsOrNull = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return null;
  if (parsed.year() < 1900) return null;
  return parsed;
};

const formatDisplayDate = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && value.startsWith('1899-')) return '-';
  if (typeof value === 'string' && value.startsWith('0000-')) return '-';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  if (parsed.year() < 1900) return '-';
  return parsed.format('YYYY-MM-DD');
};

const normalizeNameForMatch = (name) => {
  return String(name || '')
    .trim()
    .replace(/[\]\s·•.、,，:：;；()（）[【】{}“”"'`]/g, '')
    .toLowerCase();
};

const normalizeExcelDatePreview = (value) => {
  if (value == null || value === '') return '';
  if (value instanceof Date && !isNaN(value.getTime())) {
    const d = dayjs(value);
    return d.isValid() && d.year() >= 1900 ? d.format('YYYY-MM-DD') : '';
  }
  if (typeof value === 'number') {
    if (!isFinite(value) || value <= 1) return '';
    const parsed = XLSX.SSF?.parse_date_code?.(value);
    if (parsed?.y && parsed?.m && parsed?.d) {
      if (parsed.y < 1900) return '';
      const y = String(parsed.y).padStart(4, '0');
      const m = String(parsed.m).padStart(2, '0');
      const d = String(parsed.d).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '-' || trimmed === '—' || trimmed === '无') return '';
    if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
      const n = Number(trimmed);
      if (!isFinite(n) || n <= 1) return '';
      const parsed = XLSX.SSF?.parse_date_code?.(n);
      if (parsed?.y && parsed?.m && parsed?.d) {
        if (parsed.y < 1900) return '';
        const y = String(parsed.y).padStart(4, '0');
        const m = String(parsed.m).padStart(2, '0');
        const d = String(parsed.d).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
    }
    if (/^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}$/.test(trimmed)) {
      const normalized = trimmed
        .replace(/[/.]/g, '-')
        .split('-')
        .map((seg, idx) => (idx === 0 ? seg.padStart(4, '0') : seg.padStart(2, '0')))
        .join('-');
      const y = Number(normalized.slice(0, 4));
      if (!isFinite(y) || y < 1900) return '';
      if (normalized.startsWith('1899-')) return '';
      return normalized;
    }
  }
  return String(value);
};

// 已移除状态渲染相关逻辑，保留备注：如需恢复车辆状态字段可重新引入 Tag。

const EmployeeVehiclesPage = () => {
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [detail, setDetail] = useState(null); // 选中车辆详情
  const [detailOpen, setDetailOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [filterColor, setFilterColor] = useState();
  const [filterExpirySoon, setFilterExpirySoon] = useState(false);
  const [searchOwnerName, setSearchOwnerName] = useState('');
  const [searchPlateNumber, setSearchPlateNumber] = useState('');
  const searchOwnerNameRef = useRef('');
  const searchPlateNumberRef = useRef('');
  const [deletingVehicles, setDeletingVehicles] = useState(false);
  const [docUploadOpen, setDocUploadOpen] = useState(false); // 旧单文件上传弹窗切换为文档管理弹窗
  const [violationModalOpen, setViolationModalOpen] = useState(false);
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [docForm] = Form.useForm();
  const [violationForm] = Form.useForm();
  const [evaluationForm] = Form.useForm();
  // 文档上传受控文件列表
  const [docFileList, setDocFileList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const canManage = auth.hasPermission(['employee_vehicles_manage']);
  const canManageViolations = auth.hasPermission(['vehicle_violations_manage']);
  const [missingModalOpen, setMissingModalOpen] = useState(false);
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingList, setMissingList] = useState([]);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryList, setSummaryList] = useState([]);
  const [summarySoonDays, setSummarySoonDays] = useState(30);
  const [summarySearchName, setSummarySearchName] = useState('');
  const [summaryMissingOnly, setSummaryMissingOnly] = useState(false);
  const [summaryExpiringOnly, setSummaryExpiringOnly] = useState(false);
  // Excel导入弹窗
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [excelFileList, setExcelFileList] = useState([]);
  const [excelImporting, setExcelImporting] = useState(false);
  const [excelPreviewRows, setExcelPreviewRows] = useState([]);
  const [excelPreviewLoading, setExcelPreviewLoading] = useState(false);
  const [driverLicenseUploadList, setDriverLicenseUploadList] = useState([]);
  const [vehicleLicenseUploadList, setVehicleLicenseUploadList] = useState([]);
  const [licenseUploading, setLicenseUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('vehicles');
  const [vehicleViewMode, setVehicleViewMode] = useState('card'); // 默认卡片化
  const [selectedVehicleRowKeys, setSelectedVehicleRowKeys] = useState([]);
  const [noVehicleLoading, setNoVehicleLoading] = useState(false);
  const [noVehicleList, setNoVehicleList] = useState([]);
  const [departmentRegistrationGroups, setDepartmentRegistrationGroups] = useState([]);
  
  const mapDetailToFormValues = useCallback((source) => {
    if (!source) return {};
    return {
      owner_name: source.owner_name ?? '',
      body_color: source.body_color ?? '',
      plate_number: source.plate_number ?? '',
      vehicle_type: source.vehicle_type ?? '小汽车',
      driver_license_number: source.driver_license_number ?? '',
      driver_license_issue_date: toDayjsOrNull(source.driver_license_issue_date),
      driver_license_class: source.driver_license_class ?? '',
      driver_license_expiry: toDayjsOrNull(source.driver_license_expiry),
      vehicle_registration_expiry: toDayjsOrNull(source.vehicle_registration_expiry),
      vehicle_vin: source.vehicle_vin ?? '',
      vehicle_engine_number: source.vehicle_engine_number ?? ''
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if(filterColor) params.push(`body_color=${encodeURIComponent(filterColor)}`); // 后端暂未直接支持颜色筛选，可扩展；这里仅前端过滤
      const ownerKw = String(searchOwnerNameRef.current || '').trim();
      if (ownerKw) params.push(`owner_name=${encodeURIComponent(ownerKw)}`);
      const plateKw = String(searchPlateNumberRef.current || '').trim();
      if (plateKw) params.push(`plate_number=${encodeURIComponent(plateKw)}`);
      let expiringBeforeParam = '';
      if(filterExpirySoon){
        const d = new Date();
        d.setDate(d.getDate()+30); // 30天内到期
        expiringBeforeParam = d.toISOString().slice(0,10);
        params.push(`expiring_before=${expiringBeforeParam}`);
      }
      const query = params.length? ('?'+params.join('&')) : '';
      const res = await api.get('/employee-vehicles'+query);
      const list = Array.isArray(res.data?.vehicles) ? res.data.vehicles : [];
      // 如果颜色筛选在前端：
      const finalList = filterColor ? list.filter(v => v.body_color === filterColor) : list;
      setData(finalList);
      setDetail(prev => {
        if (!prev) return prev;
        const hit = finalList.find(item => item.id === prev.id);
        return hit ? { ...prev, ...hit } : prev;
      });
    } catch (err) {
      message.error(err?.response?.data?.message || '加载车辆数据失败');
    } finally {
      setLoading(false);
    }
  }, [filterColor, filterExpirySoon]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    searchOwnerNameRef.current = searchOwnerName;
  }, [searchOwnerName]);

  useEffect(() => {
    searchPlateNumberRef.current = searchPlateNumber;
  }, [searchPlateNumber]);

  const handleDeleteSelectedVehicles = async () => {
    if (!canManage) return;
    if (vehicleViewMode !== 'list' || !selectedVehicleRowKeys.length) {
      message.warning('请先在“列表”模式勾选要删除的车辆');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: `将删除已选择的 ${selectedVehicleRowKeys.length} 条车辆记录（相关文档/评估/违章将一并删除）。是否继续？`,
      okText: '删除',
      okButtonProps: { danger: true, loading: deletingVehicles },
      cancelText: '取消',
      onOk: async () => {
        setDeletingVehicles(true);
        try {
          await api.post('/employee-vehicles/batch-delete', { ids: selectedVehicleRowKeys });
          message.success('删除成功');
          setSelectedVehicleRowKeys([]);
          fetchData();
        } catch (e) {
          message.error(e?.response?.data?.error || '删除失败');
        } finally {
          setDeletingVehicles(false);
        }
      }
    });
  };

  const fetchNoVehicleEmployees = useCallback(async () => {
    setNoVehicleLoading(true);
    try {
      // 规则：按“车辆列表”的车主姓名(owner_name)与员工姓名比对；命中则视为已登记
      // 兼容历史/导入数据：即便 vehicle.employee_id 为空，也可通过姓名命中
      const vehiclesReq = api.get('/employee-vehicles').catch((err) => ({ __error: err }));
      const summaryReq = api.get('/employee-vehicles/compliance/summary', { params: { soon_days: 30, active_only: 1 } });
      const [vehiclesRes, summaryRes] = await Promise.all([vehiclesReq, summaryReq]);

      const ownerNameCountMap = new Map();
      if (!vehiclesRes?.__error) {
        const vehicles = Array.isArray(vehiclesRes.data?.vehicles) ? vehiclesRes.data.vehicles : [];
        for (const v of vehicles) {
          const rawName = v?.owner_name || v?.employee_name || '';
          const key = normalizeNameForMatch(rawName);
          if (!key) continue;
          ownerNameCountMap.set(key, (ownerNameCountMap.get(key) || 0) + 1);
        }
      }

      const listRaw = Array.isArray(summaryRes.data?.employees) ? summaryRes.data.employees : [];
      const list = listRaw.map((r) => {
        const nameKey = normalizeNameForMatch(r?.employee_name);
        const listCount = nameKey ? Number(ownerNameCountMap.get(nameKey) || 0) : 0;
        const backendCount = Number(r?.vehicles_count || 0);
        const backendHas = !!r?.has_vehicle && backendCount > 0;
        const hasVehicle = backendHas || listCount > 0;
        const vehicles_count = backendCount > 0 ? backendCount : listCount;
        return { ...r, has_vehicle: hasVehicle ? 1 : 0, vehicles_count };
      });

      const groupsMap = new Map();
      for (const r of list) {
        const dept = (r?.department && String(r.department).trim()) ? String(r.department).trim() : '未填写部门';
        if (!groupsMap.has(dept)) {
          groupsMap.set(dept, { department: dept, registered: [], unregistered: [] });
        }
        const g = groupsMap.get(dept);
        const hasVehicle = !!r?.has_vehicle && Number(r?.vehicles_count || 0) > 0;
        if (hasVehicle) g.registered.push(r);
        else g.unregistered.push(r);
      }

      const groups = Array.from(groupsMap.values()).sort((a, b) => String(a.department).localeCompare(String(b.department), 'zh-CN'));
      for (const g of groups) {
        g.registered.sort((a, b) => String(a.employee_name || '').localeCompare(String(b.employee_name || ''), 'zh-CN'));
        g.unregistered.sort((a, b) => String(a.employee_name || '').localeCompare(String(b.employee_name || ''), 'zh-CN'));
      }

      setDepartmentRegistrationGroups(groups);

      // 仍保留“未登记”扁平列表用于导出
      const filtered = list.filter(r => !r?.has_vehicle || Number(r?.vehicles_count || 0) === 0);
      setNoVehicleList(filtered);
    } catch (e) {
      message.error(e?.response?.data?.error || '获取车辆登记情况失败');
      setNoVehicleList([]);
      setDepartmentRegistrationGroups([]);
    } finally {
      setNoVehicleLoading(false);
    }
  }, []);

  const exportNoVehicleEmployees = () => {
    try {
      const rows = (noVehicleList || []).map(r => ({
        '员工ID': r.employee_id,
        '姓名': r.employee_name,
        '车辆数': Number(r.vehicles_count || 0)
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '未登记车辆');
      const fileName = `未登记车辆员工_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      message.success('导出完成');
    } catch (e) {
      message.error('导出失败');
    }
  };

  const openDetail = (record) => {
    setDetail({ ...record });
    setDriverLicenseUploadList([]);
    setVehicleLicenseUploadList([]);
    editForm.resetFields();
    editForm.setFieldsValue(mapDetailToFormValues(record));
    setDetailOpen(true);
  };

  const openCreate = () => {
    const draft = {
      plate_number: '',
      owner_name: null,
      vehicle_type: '小汽车',
      body_color: null,
      driver_license_number: null,
      driver_license_issue_date: null,
      driver_license_class: null,
      driver_license_expiry: null,
      vehicle_registration_expiry: null,
      vehicle_vin: null,
      vehicle_engine_number: null
    };
    openDetail(draft);
  };

  const openDetailForEmployeeWithoutVehicle = (employeeRow) => {
    // 复用车辆详情弹窗（创建模式：detail 无 id）
    const draft = {
      employee_id: employeeRow?.employee_id,
      owner_name: employeeRow?.employee_name || null,
      plate_number: '',
      vehicle_type: '小汽车',
      body_color: null,
      driver_license_number: null,
      driver_license_issue_date: null,
      driver_license_class: null,
      driver_license_expiry: null,
      vehicle_registration_expiry: null,
      vehicle_vin: null,
      vehicle_engine_number: null
    };
    openDetail(draft);
  };

  // 保证在弹窗打开后赋值；如果为空则给默认占位符避免空白
  useEffect(() => {
    if(detailOpen && detail){
      editForm.setFieldsValue(mapDetailToFormValues(detail));
    }
  }, [detailOpen, detail, editForm, mapDetailToFormValues]);

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      const payload = {
        owner_name: values.owner_name || null,
        body_color: values.body_color || null,
        plate_number: values.plate_number,
        vehicle_type: values.vehicle_type,
        driver_license_number: values.driver_license_number || null,
        driver_license_issue_date: values.driver_license_issue_date ? values.driver_license_issue_date.format('YYYY-MM-DD') : null,
        driver_license_class: values.driver_license_class || null,
        driver_license_expiry: values.driver_license_expiry ? values.driver_license_expiry.format('YYYY-MM-DD') : null,
        vehicle_registration_expiry: values.vehicle_registration_expiry ? values.vehicle_registration_expiry.format('YYYY-MM-DD') : null,
        vehicle_vin: values.vehicle_vin || null,
        vehicle_engine_number: values.vehicle_engine_number || null
      };
      if (detail?.id) {
        await api.patch(`/employee-vehicles/${detail.id}`, payload);
        message.success('车辆信息已更新');
      } else {
        // 创建模式：关联 employee_id
        await api.post('/employee-vehicles', { ...payload, employee_id: detail?.employee_id || null });
        message.success('车辆信息已登记');
      }
      setDetailOpen(false);
      fetchData();
      if (activeTab === 'no_vehicle') fetchNoVehicleEmployees();
    } catch(err){
      message.error(err?.response?.data?.error || err?.response?.data?.message || '更新失败');
    }
  };

  const columns = [
    { title: '车牌号', dataIndex: 'plate_number', key: 'plate_number', render: v => v || '-' },
    { title: '车主姓名', dataIndex: 'owner_name', key: 'owner_name', render: v => v || '-' },
    { title: '颜色', dataIndex: 'body_color', key: 'body_color', render: v => v || '-' },
    { title: '车辆类型', dataIndex: 'vehicle_type', key: 'vehicle_type', render: v => v || '-' },
    {
      title: '驾驶证',
      key: 'driver_license_image_url',
      render: (_, record) => {
        const previewUrl = buildMinioProxyUrl(record.driver_license_image_object || record.driver_license_image_url) || record.driver_license_image_url;
        if (!previewUrl) return <span style={{ color: '#999' }}>未上传</span>;
        return (
          <button
            type="button"
            onClick={() => openWithAuth(previewUrl)}
            style={{ display: 'inline-block', padding: 0, border: 0, background: 'transparent', cursor: 'pointer' }}
          >
            <AuthedImage
              src={previewUrl}
              alt="驾驶证"
              style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4, background: '#f5f5f5' }}
            />
          </button>
        );
      }
    },
    {
      title: '行驶证',
      key: 'vehicle_license_image_url',
      render: (_, record) => {
        const previewUrl = buildMinioProxyUrl(record.vehicle_license_image_object || record.vehicle_license_image_url) || record.vehicle_license_image_url;
        if (!previewUrl) return <span style={{ color: '#999' }}>未上传</span>;
        return (
          <button
            type="button"
            onClick={() => openWithAuth(previewUrl)}
            style={{ display: 'inline-block', padding: 0, border: 0, background: 'transparent', cursor: 'pointer' }}
          >
            <AuthedImage
              src={previewUrl}
              alt="行驶证"
              style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4, background: '#f5f5f5' }}
            />
          </button>
        );
      }
    },
    { title: '驾驶证到期', dataIndex: 'driver_license_expiry', key: 'driver_license_expiry', render: formatDisplayDate },
    { title: '行驶证到期', dataIndex: 'vehicle_registration_expiry', key: 'vehicle_registration_expiry', render: formatDisplayDate },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openDetail(record)}>详情</Button>
          {canManage && <Button size="small" type="link" onClick={() => { setDetail(record); openDocuments(record); }}>文档</Button>}
          {canManage && <Button size="small" type="link" onClick={() => { setDetail(record); setEvaluationModalOpen(true); }}>评估</Button>}
          {(canManageViolations || canManage) && <Button size="small" type="link" danger onClick={() => { setDetail(record); setViolationModalOpen(true); }}>违章</Button>}
        </Space>
      )
    }
  ];

  const vehicleRowSelection = {
    selectedRowKeys: selectedVehicleRowKeys,
    onChange: (keys) => setSelectedVehicleRowKeys(keys),
    preserveSelectedRowKeys: true
  };

  // 文档上传
  const openDocuments = async (record) => {
    setDetail(record);
    setDocUploadOpen(true);
    setDocFileList([]);
    docForm.resetFields();
    await fetchDocuments(record.id);
  };

  const fetchDocuments = async (vehicleId = detail?.id) => {
    if(!vehicleId) return;
    setDocLoading(true);
    try {
      const res = await api.get(`/employee-vehicles/${vehicleId}/documents`);
      setDocuments(Array.isArray(res.data?.documents) ? res.data.documents : []);
    } catch(e){
      message.error(e?.response?.data?.error || '获取文档失败');
    } finally { setDocLoading(false); }
  };

  const handleUploadDoc = async () => {
    try {
      const values = await docForm.validateFields();
      if(!docFileList.length) return message.error('请选择文件');
      const formData = new FormData();
      formData.append('doc_type', values.doc_type);
      if(values.expiry_date){ formData.append('expiry_date', values.expiry_date.format('YYYY-MM-DD')); }
      // 多文件支持
      docFileList.forEach(f => formData.append('files', f.originFileObj || f));
      // 让浏览器/axios 自动设置 multipart boundary，避免后端收不到文件
      await api.post(`/employee-vehicles/${detail.id}/documents`, formData);
      message.success('上传成功');
      setDocFileList([]);
      docForm.resetFields();
      fetchDocuments();
    } catch(e){
      message.error(e?.response?.data?.error || '上传失败');
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await api.delete(`/employee-vehicles/documents/${docId}`);
      message.success('文档已删除');
      fetchDocuments();
    } catch(e){
      message.error(e?.response?.data?.error || '删除失败');
    }
  };

  const openMissing = async () => {
    setMissingModalOpen(true);
    setMissingLoading(true);
    try {
      const res = await api.get('/employee-vehicles/missing/compliance');
      setMissingList(Array.isArray(res.data?.employees) ? res.data.employees : []);
    } catch(e){
      message.error(e?.response?.data?.error || '获取缺失合规列表失败');
    } finally { setMissingLoading(false); }
  };

  const openSummary = async () => {
    setSummaryModalOpen(true);
    setSummaryLoading(true);
    try {
      const res = await api.get(`/employee-vehicles/compliance/summary?soon_days=${summarySoonDays}`);
      setSummaryList(Array.isArray(res.data?.employees) ? res.data.employees : []);
    } catch(e){
      message.error(e?.response?.data?.error || '获取合规总览失败');
    } finally { setSummaryLoading(false); }
  };

  // 违章记录
  const handleCreateViolation = async () => {
    try {
      const values = await violationForm.validateFields();
      const payload = {
        violation_type: values.violation_type,
        description: values.description,
        severity: values.severity,
        penalty_points: values.penalty_points || null,
        occurred_at: values.occurred_at.format('YYYY-MM-DD HH:mm:ss')
      };
      await api.post(`/employee-vehicles/${detail.id}/violations`, payload);
      message.success('违章记录已添加');
      setViolationModalOpen(false);
      violationForm.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.message || '添加失败');
    }
  };

  // 车况评估
  const handleCreateEvaluation = async () => {
    try {
      const values = await evaluationForm.validateFields();
      const payload = {
        evaluate_date: values.evaluate_date.format('YYYY-MM-DD'),
        score: values.score,
        issues: values.issues || null,
        recommendation: values.recommendation || null
      };
      await api.post(`/employee-vehicles/${detail.id}/evaluations`, payload);
      message.success('评估记录已添加');
      setEvaluationModalOpen(false);
      evaluationForm.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.message || '添加失败');
    }
  };

  const handleUploadLicenseImages = async () => {
    if (!detail?.id) {
      message.warning('请先登记车辆信息后再上传证件图片');
      return;
    }
    if (!driverLicenseUploadList.length && !vehicleLicenseUploadList.length) {
      message.warning('请选择需要上传的证件图片');
      return;
    }
    const formData = new FormData();
    if (driverLicenseUploadList.length) {
      formData.append('driver_license', driverLicenseUploadList[0].originFileObj || driverLicenseUploadList[0]);
    }
    if (vehicleLicenseUploadList.length) {
      formData.append('vehicle_license', vehicleLicenseUploadList[0].originFileObj || vehicleLicenseUploadList[0]);
    }
    setLicenseUploading(true);
    try {
      // 让浏览器/axios 自动设置 multipart boundary，避免后端收不到文件
      const res = await api.post(`/employee-vehicles/${detail.id}/license-images`, formData);
      const updatePayload = res.data?.vehicle || {};
      setDetail(prev => (prev ? { ...prev, ...updatePayload } : prev));
      fetchData();
      setDriverLicenseUploadList([]);
      setVehicleLicenseUploadList([]);
      message.success('证件图片已更新');
    } catch (err) {
      message.error(err?.response?.data?.error || err?.response?.data?.message || err.message || '上传失败');
    } finally {
      setLicenseUploading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Space style={{ flexWrap:'wrap' }}>
          <Button icon={<UploadOutlined />} type="default" onClick={() => setExcelImportOpen(true)}>Excel导入</Button>
      <Modal
        title="批量导入车辆数据（Excel）"
        open={excelImportOpen}
        onCancel={() => { setExcelImportOpen(false); setExcelFileList([]); }}
        footer={null}
        destroyOnClose
      >
        <Upload
          beforeUpload={() => false}
          fileList={excelFileList}
          onChange={({fileList}) => {
            setExcelFileList(fileList);
            // 清除上次预览
            setExcelPreviewRows([]);
          }}
          accept=".xls,.xlsx"
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>选择Excel文件</Button>
        </Upload>
        <div style={{margin:'12px 0', display:'flex', gap:8}}>
          <Button
            type="default"
            loading={excelPreviewLoading}
            disabled={!excelFileList.length}
            onClick={async () => {
              if (!excelFileList.length) return message.error('请先选择文件');
              setExcelPreviewLoading(true);
              try {
                const file = excelFileList[0].originFileObj || excelFileList[0];
                const data = await file.arrayBuffer();
                const wb = XLSX.read(data, { type: 'array' });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                // 简单表头映射，优先展示关键信息
                const headerMap = {
                  '姓名': 'owner_name',
                  '车主姓名': 'owner_name',
                  '车主': 'owner_name',
                  'owner': 'owner_name',
                  'owner_name': 'owner_name',
                  '车辆类型': 'vehicle_type',
                  '请上传行驶证照片:车辆类型': 'vehicle_type',
                  '证件号码': 'driver_license_number',
                  '初次领证日期': 'driver_license_issue_date',
                  '准驾车型': 'driver_license_class',
                  '有效期限': 'driver_license_expiry',
                  '请上传驾驶证照片:准驾车型': 'driver_license_class',
                  '请上传驾驶证照片:证件号码': 'driver_license_number',
                  '请上传驾驶证照片:初次领证日期': 'driver_license_issue_date',
                  '请上传驾驶证照片:有效期限': 'driver_license_expiry',
                  '车牌号码': 'plate_number',
                  '请上传行驶证照片:车牌号码': 'plate_number',
                  '车牌号': 'plate_number',
                  '车牌': 'plate_number',
                  'license_plate': 'plate_number',
                  '所有人姓名': 'owner_name',
                  '请上传行驶证照片:所有人姓名': 'owner_name',
                  '车辆识别代号': 'vehicle_vin',
                  '请上传行驶证照片:车辆识别代号': 'vehicle_vin',
                  '发动机号码': 'vehicle_engine_number',
                  '请上传行驶证照片:发动机号码': 'vehicle_engine_number',
                  '请上传驾驶证照片:驾驶证:图片_1': 'driver_license_image',
                  '请上传行驶证照片:行驶证:图片_1': 'vehicle_license_image',
                  '驾驶证': 'driver_license_image',
                  '行驶证': 'vehicle_license_image',
                  '驾驶证图片(base64)': 'driver_license_image',
                  '行驶证图片(base64)': 'vehicle_license_image'
                };
                const parsed = [];
                for (const r of rows.slice(0, 1000)) {
                  const v = {};
                  for (const k in r) {
                    const key = k && k.toString().trim();
                    const mapKey = headerMap[key] || null;
                    if (mapKey) {
                      let cellValue = r[k];
                      if (mapKey === 'driver_license_issue_date' || mapKey === 'driver_license_expiry') {
                        cellValue = normalizeExcelDatePreview(cellValue);
                      }
                      v[mapKey] = cellValue;
                    }
                  }
                  // keep raw row for debugging
                  v.__raw = r;
                  parsed.push(v);
                }
                setExcelPreviewRows(parsed);
                if (!parsed.length) message.warning('未识别到有效数据，请检查表头');
              } catch (e) {
                console.error(e);
                message.error('解析文件失败');
              } finally { setExcelPreviewLoading(false); }
            }}
          >解析并预览</Button>

          <Button
            type="primary"
            loading={excelImporting}
            disabled={!excelFileList.length || !excelPreviewRows.length}
            onClick={async () => {
              if (!excelFileList.length) return message.error('请先选择文件');
              if (!excelPreviewRows.length) return message.error('请先解析并确认预览数据');
              setExcelImporting(true);
              try {
                const formData = new FormData();
                formData.append('file', excelFileList[0].originFileObj || excelFileList[0]);
                const res = await api.post('/employee-vehicles/import/excel', formData, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                  timeout: 60000
                });
                message.success(res.data?.message || '导入成功');
                setExcelImportOpen(false);
                setExcelFileList([]);
                setExcelPreviewRows([]);
                fetchData();
              } catch (err) {
                message.error(err?.response?.data?.message || '导入失败');
              }
              setExcelImporting(false);
            }}
          >确认导入到数据库</Button>

          <Button style={{marginLeft:8}} onClick={() => { setExcelFileList([]); setExcelPreviewRows([]); }} disabled={!excelFileList.length}>清空</Button>
        </div>
        {excelPreviewRows.length ? (
          <div style={{maxHeight:360, overflow:'auto', marginTop:12}}>
            <Table
              size="small"
              dataSource={excelPreviewRows.map((r, idx) => ({...r, key: idx}))}
              pagination={false}
              columns={[
                { title: '姓名', dataIndex: 'owner_name', key: 'owner_name' },
                { title: '车辆类型', dataIndex: 'vehicle_type', key: 'vehicle_type' },
                { title: '驾驶证号', dataIndex: 'driver_license_number', key: 'driver_license_number' },
                { title: '初次领证日期', dataIndex: 'driver_license_issue_date', key: 'driver_license_issue_date' },
                { title: '准驾车型', dataIndex: 'driver_license_class', key: 'driver_license_class' },
                { title: '驾驶证到期', dataIndex: 'driver_license_expiry', key: 'driver_license_expiry' },
                { title: '行驶证车牌', dataIndex: 'plate_number', key: 'plate_number' },
                { title: '行驶证车辆类型', dataIndex: 'vehicle_type', key: 'vehicle_type_preview' },
                { title: '行驶证所有人', dataIndex: 'owner_name', key: 'owner_name_license' },
                { title: 'VIN', dataIndex: 'vehicle_vin', key: 'vehicle_vin' },
                { title: '发动机号', dataIndex: 'vehicle_engine_number', key: 'vehicle_engine_number' },
                { title: '驾驶证图片(base64)', dataIndex: 'driver_license_image', key: 'driver_license_image', render: v => v ? '已包含' : '-' },
                { title: '行驶证图片(base64)', dataIndex: 'vehicle_license_image', key: 'vehicle_license_image', render: v => v ? '已包含' : '-' }
              ]}
            />
            <div style={{fontSize:12, color:'#666', marginTop:8}}>预览已显示解析到的所有可用字段，确认无误后点击“确认导入到数据库”。</div>
          </div>
        ) : null}
        <div style={{fontSize:'12px',color:'#888'}}>支持表头：姓名、车辆类型、证件号码、初次领证日期、准驾车型、有效期限、车牌号码、所有人姓名、车辆识别代号、发动机号码、驾驶证/驾驶证图片(base64)、行驶证/行驶证图片(base64)；也兼容：请上传驾驶证照片:*、请上传行驶证照片:*。</div>
      </Modal>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>刷新</Button>
          {canManage && <Button icon={<PlusOutlined />} type="primary" onClick={openCreate}>新增车辆</Button>}
          <Button icon={<FileAddOutlined />} type="default" onClick={() => {
          Modal.confirm({
            title: '批量导入车辆数据',
            content: '此操作将一次性导入预设的车辆数据，如已导入请勿重复操作。是否继续？',
            okText: '确认导入',
            cancelText: '取消',
            onOk: async () => {
              try {
                const res = await api.post('/employee-vehicles/import');
                message.success(res.data?.message || '导入成功');
                fetchData();
              } catch (err) {
                message.error(err?.response?.data?.message || '导入失败');
              }
            }
          });
          }}>导入车辆数据</Button>
          <Button icon={<WarningOutlined />} onClick={() => openMissing()} loading={missingLoading}>缺失合规</Button>
          <Button onClick={() => openSummary()} loading={summaryLoading}>合规总览</Button>
          <Select
          allowClear
          placeholder="颜色筛选"
          style={{ width:120 }}
          value={filterColor}
          onChange={v => setFilterColor(v)}
          options={[...new Set(data.map(d=>d.body_color).filter(Boolean))].map(c=>({value:c,label:c}))}
        />
          <Input
          allowClear
          placeholder="姓名检索"
          style={{ width: 140 }}
          value={searchOwnerName}
          onChange={(e) => {
            const next = e.target.value;
            setSearchOwnerName(next);
            searchOwnerNameRef.current = next;
            if (!String(next || '').trim()) fetchData();
          }}
          onPressEnter={fetchData}
        />
          <Input
          allowClear
          placeholder="车牌检索"
          style={{ width: 140 }}
          value={searchPlateNumber}
          onChange={(e) => {
            const next = e.target.value;
            setSearchPlateNumber(next);
            searchPlateNumberRef.current = next;
            if (!String(next || '').trim()) fetchData();
          }}
          onPressEnter={fetchData}
        />
          <Checkbox checked={filterExpirySoon} onChange={e=>setFilterExpirySoon(e.target.checked)}>30天内到期</Checkbox>
        </Space>

        <Space>
          {canManage && (
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={vehicleViewMode !== 'list' || !selectedVehicleRowKeys.length}
              loading={deletingVehicles}
              onClick={handleDeleteSelectedVehicles}
            >
              删除
            </Button>
          )}
        </Space>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={(k) => {
          setActiveTab(k);
          if (k === 'no_vehicle') fetchNoVehicleEmployees();
        }}
        items={[
          {
            key: 'vehicles',
            label: `车辆列表（${data.length || 0}）`,
            children: (
              <div>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Radio.Group
                    value={vehicleViewMode}
                    onChange={(e) => {
                      const next = e.target.value;
                      setVehicleViewMode(next);
                      if (next !== 'list') setSelectedVehicleRowKeys([]);
                    }}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                    options={[
                      { label: '卡片', value: 'card' },
                      { label: '列表', value: 'list' }
                    ]}
                  />
                  {vehicleViewMode === 'list' ? (
                    <span style={{ color: '#888', fontSize: 12 }}>已选择 {selectedVehicleRowKeys.length} 条</span>
                  ) : null}
                </Space>

                {vehicleViewMode === 'card' ? (
                  <List
                    loading={loading}
                    dataSource={data}
                    locale={{ emptyText: <Empty description="暂无车辆数据" /> }}
                    grid={{ gutter: 12, xs: 1, sm: 2, md: 3, lg: 4, xl: 4, xxl: 5 }}
                    pagination={{ pageSize: 20, showSizeChanger: false }}
                    renderItem={(item) => (
                      <List.Item>
                        <Card
                          size="small"
                          hoverable
                          onClick={() => openDetail(item)}
                          bodyStyle={{ padding: 10 }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.plate_number || ''}>
                              {item.plate_number || '-'}
                            </div>
                            <Tag color="blue" style={{ marginInlineEnd: 0 }}>{item.vehicle_type || '其他'}</Tag>
                          </div>
                          <div style={{ marginTop: 6, color: '#555', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.owner_name || ''}>
                            姓名：{item.owner_name || '-'}
                          </div>
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Tag color={item.driver_license_image_url ? 'green' : 'red'}>{item.driver_license_image_url ? '驾驶证' : '缺驾驶证'}</Tag>
                            <Tag color={item.vehicle_license_image_url ? 'green' : 'red'}>{item.vehicle_license_image_url ? '行驶证' : '缺行驶证'}</Tag>
                          </div>
                          <div style={{ marginTop: 6, color: '#888', fontSize: 12 }}>
                            驾驶证到期：{formatDisplayDate(item.driver_license_expiry)}
                          </div>
                          <div style={{ marginTop: 2, color: '#888', fontSize: 12 }}>
                            行驶证到期：{formatDisplayDate(item.vehicle_registration_expiry)}
                          </div>
                        </Card>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={data}
                    columns={columns}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: <Empty description="暂无车辆数据" /> }}
                    rowSelection={vehicleRowSelection}
                  />
                )}
              </div>
            )
          },
          {
            key: 'no_vehicle',
            label: `按部门登记情况（未登记 ${noVehicleList.length || 0}）`,
            children: (
              <div>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Button onClick={fetchNoVehicleEmployees} loading={noVehicleLoading}>刷新</Button>
                  <Button type="primary" onClick={exportNoVehicleEmployees} disabled={!noVehicleList.length}>导出Excel</Button>
                </Space>
                {departmentRegistrationGroups.length ? (
                  <Collapse
                    accordion
                    items={departmentRegistrationGroups.map((g) => {
                      const deptTitle = `${g.department}（已登记 ${g.registered.length} / 未登记 ${g.unregistered.length}）`;
                      return {
                        key: g.department,
                        label: deptTitle,
                        children: (
                          <Row gutter={[12, 12]}>
                            <Col xs={24} md={12}>
                              <div style={{ fontWeight: 600, marginBottom: 8 }}>已登记</div>
                              <Table
                                size="small"
                                rowKey="employee_id"
                                dataSource={g.registered}
                                pagination={{ pageSize: 8, hideOnSinglePage: true }}
                                locale={{ emptyText: <Empty description="暂无" /> }}
                                columns={[
                                  { title: '姓名', dataIndex: 'employee_name', key: 'employee_name' },
                                  { title: '车辆数', dataIndex: 'vehicles_count', key: 'vehicles_count', width: 90, render: v => Number(v || 0) }
                                ]}
                              />
                            </Col>
                            <Col xs={24} md={12}>
                              <div style={{ fontWeight: 600, marginBottom: 8 }}>未登记</div>
                              <Table
                                size="small"
                                rowKey="employee_id"
                                dataSource={g.unregistered}
                                pagination={{ pageSize: 8, hideOnSinglePage: true }}
                                locale={{ emptyText: <Empty description="暂无" /> }}
                                columns={[
                                  { title: '姓名', dataIndex: 'employee_name', key: 'employee_name' },
                                  {
                                    title: '操作',
                                    key: 'actions',
                                    width: 120,
                                    render: (_, r) => canManage ? (
                                      <Button size="small" type="link" onClick={() => openDetailForEmployeeWithoutVehicle(r)}>登记车辆</Button>
                                    ) : <span style={{ color: '#999' }}>无权限</span>
                                  }
                                ]}
                              />
                            </Col>
                          </Row>
                        )
                      };
                    })}
                  />
                ) : (
                  <Empty description="暂无在职员工数据" />
                )}
              </div>
            )
          }
        ]}
      />

      {/* 合规总览 */}
      <Modal
        title={`员工车辆/证件合规总览（到期预警 ${summarySoonDays} 天内）`}
        open={summaryModalOpen}
        onCancel={()=> setSummaryModalOpen(false)}
        footer={null}
        width={880}
        destroyOnClose
      >
        <Space style={{marginBottom:12}} wrap>
            <InputNumber min={7} max={180} value={summarySoonDays} onChange={v=> setSummarySoonDays(v||30)} />
            <Input placeholder="按姓名搜索" style={{width:160}} value={summarySearchName} onChange={e=> setSummarySearchName(e.target.value)} />
            <Checkbox checked={summaryMissingOnly} onChange={e=> setSummaryMissingOnly(e.target.checked)}>仅显示缺失</Checkbox>
            <Checkbox checked={summaryExpiringOnly} onChange={e=> setSummaryExpiringOnly(e.target.checked)}>仅即将到期</Checkbox>
            <Button size="small" onClick={()=> openSummary()}>刷新</Button>
        </Space>
        <Table
          rowKey="employee_id"
            loading={summaryLoading}
            dataSource={summaryList.filter(r => {
              if(summarySearchName && !String(r.employee_name||'').includes(summarySearchName)) return false;
              if(summaryMissingOnly && !(r.missing_items&&r.missing_items.length)) return false;
              if(summaryExpiringOnly && !(r.expiring_soon&&r.expiring_soon.length)) return false;
              return true;
            })}
          pagination={{pageSize:12}}
          locale={{emptyText:<Empty description="暂无员工" />}}
          columns={[
            { title:'员工ID', dataIndex:'employee_id', key:'employee_id' },
            { title:'姓名', dataIndex:'employee_name', key:'employee_name' },
            { title:'车辆数', dataIndex:'vehicles_count', key:'vehicles_count' },
            { title:'车辆', dataIndex:'has_vehicle', key:'has_vehicle', render:v=> v? <Tag color="green">有</Tag>: <Tag color="red">无</Tag> },
            { title:'驾驶证', dataIndex:'has_driver_license', key:'has_driver_license', render:(v,r)=> v? <Tag color={r.driver_license_expiring_soon? 'orange':'green'}>{r.driver_license_expiring_soon? '即将到期':'已上传'}</Tag>: <Tag color="red">缺失</Tag> },
            { title:'行驶证', dataIndex:'has_vehicle_registration', key:'has_vehicle_registration', render:(v,r)=> v? <Tag color={r.vehicle_registration_expiring_soon? 'orange':'green'}>{r.vehicle_registration_expiring_soon? '即将到期':'已上传'}</Tag>: <Tag color="red">缺失</Tag> },
            { title:'缺失项目', dataIndex:'missing_items', key:'missing_items', render: arr => Array.isArray(arr)&&arr.length? arr.map(i=> <Tag color="volcano" key={i}>{i}</Tag>) : '-' },
            { title:'到期预警', dataIndex:'expiring_soon', key:'expiring_soon', render: arr => Array.isArray(arr)&&arr.length? arr.map(i=> <Tag color="orange" key={i}>{i}</Tag>) : '-' }
          ]}
        />
      </Modal>

      <Modal
        title={detail?.id ? `车辆详情编辑 - ${detail.plate_number}` : '车辆详情登记'}
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDriverLicenseUploadList([]);
          setVehicleLicenseUploadList([]);
        }}
        onOk={handleUpdate}
        okText={detail?.id ? '保存修改' : '登记'}
        cancelText="关闭"
        destroyOnClose
        width={720}
      >
        {detail && (
          <Form layout="vertical" form={editForm} key={detail.id || 'create'}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="owner_name" label="车主姓名">
                  <Input placeholder="请输入车主姓名" allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="plate_number" label="车牌号" rules={[{ required: true, message: '车牌号必填' }]}>
                  <Input placeholder="例如 京A12345" allowClear />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="vehicle_type" label="车辆类型" rules={[{ required: true, message: '请选择车辆类型' }]}> 
                  <Select 
                    options={vehicleTypeOptions.map(v=>({value:v,label:v}))}
                    placeholder="选择车辆类型"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="body_color" label="车身颜色">
                  <Input placeholder="例如 黑/白/红黑" allowClear />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="driver_license_number" label="驾驶证号码">
                  <Input placeholder="请输入驾驶证号码" allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="driver_license_class" label="准驾车型">
                  <Input placeholder="例如 C1/B2" allowClear />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="driver_license_issue_date" label="驾驶证初领日期">
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="选择日期" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="driver_license_expiry" label="驾驶证到期日">
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="选择日期" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="vehicle_registration_expiry" label="行驶证到期日">
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="选择日期" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="vehicle_engine_number" label="发动机号">
                  <Input placeholder="请输入发动机号" allowClear />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="vehicle_vin" label="车辆VIN">
                  <Input placeholder="输入17位VIN码" allowClear />
                </Form.Item>
              </Col>
            </Row>
            {canManage && (
              <>
                <Form.Item label="驾驶证图片">
                  <Space direction="vertical" size={8}>
                    {(() => {
                      const previewUrl = buildMinioProxyUrl(detail.driver_license_image_object || detail.driver_license_image_url) || detail.driver_license_image_url;
                      if (!previewUrl) return <span style={{ color: '#999' }}>未上传</span>;
                      return (
                        <button
                          type="button"
                          onClick={() => openWithAuth(previewUrl)}
                          style={{ display: 'inline-block', padding: 0, border: 0, background: 'transparent', cursor: 'pointer' }}
                        >
                          <AuthedImage
                            src={previewUrl}
                            alt="驾驶证"
                            style={{ width: 180, height: 128, objectFit: 'cover', borderRadius: 6, background: '#f5f5f5' }}
                          />
                        </button>
                      );
                    })()}
                    <Upload
                      beforeUpload={() => false}
                      fileList={driverLicenseUploadList}
                      maxCount={1}
                      accept="image/*"
                      onChange={({ fileList }) => setDriverLicenseUploadList(fileList.slice(-1))}
                    >
                      <Button icon={<UploadOutlined />}>选择驾驶证图片</Button>
                    </Upload>
                  </Space>
                </Form.Item>
                <Form.Item label="行驶证图片">
                  <Space direction="vertical" size={8}>
                    {(() => {
                      const previewUrl = buildMinioProxyUrl(detail.vehicle_license_image_object || detail.vehicle_license_image_url) || detail.vehicle_license_image_url;
                      if (!previewUrl) return <span style={{ color: '#999' }}>未上传</span>;
                      return (
                        <button
                          type="button"
                          onClick={() => openWithAuth(previewUrl)}
                          style={{ display: 'inline-block', padding: 0, border: 0, background: 'transparent', cursor: 'pointer' }}
                        >
                          <AuthedImage
                            src={previewUrl}
                            alt="行驶证"
                            style={{ width: 180, height: 128, objectFit: 'cover', borderRadius: 6, background: '#f5f5f5' }}
                          />
                        </button>
                      );
                    })()}
                    <Upload
                      beforeUpload={() => false}
                      fileList={vehicleLicenseUploadList}
                      maxCount={1}
                      accept="image/*"
                      onChange={({ fileList }) => setVehicleLicenseUploadList(fileList.slice(-1))}
                    >
                      <Button icon={<UploadOutlined />}>选择行驶证图片</Button>
                    </Upload>
                  </Space>
                </Form.Item>
                <Space>
                  <Button
                    type="primary"
                    ghost
                    onClick={handleUploadLicenseImages}
                    loading={licenseUploading}
                    disabled={!driverLicenseUploadList.length && !vehicleLicenseUploadList.length}
                  >
                    上传证件图片
                  </Button>
                </Space>
              </>
            )}
            {/* 状态字段按需求移除 */}
          </Form>
        )}
        {!detail && <Empty description="未选择车辆" />}
      </Modal>

      {/* 文档管理：列表 + 多文件上传 */}
      <Modal
        title={detail ? `车辆文档 - ${detail.plate_number}` : '车辆文档'}
        open={docUploadOpen}
        onCancel={() => setDocUploadOpen(false)}
        footer={null}
        width={760}
        destroyOnClose
      >
        <Space direction="vertical" style={{width:'100%'}} size="middle">
          <Form layout="inline" form={docForm} onFinish={handleUploadDoc} style={{flexWrap:'wrap'}}>
            <Form.Item name="doc_type" label="文档类型" rules={[{ required: true }]}> 
              <Select style={{minWidth:140}} options={['驾驶证','行驶证','身份证','保险单','车况照片','其他'].map(v=>({value:v,label:v}))} />
            </Form.Item>
            <Form.Item name="expiry_date" label="到期日期"> <DatePicker /> </Form.Item>
            <Upload 
              beforeUpload={() => false} 
              multiple 
              fileList={docFileList} 
              onChange={({fileList})=> setDocFileList(fileList)}
              style={{marginRight:8}}
            > 
              <Button icon={<FileAddOutlined />}>选择文件</Button> 
            </Upload>
            <Button type="primary" htmlType="submit" disabled={!docFileList.length}>上传</Button>
            <Button onClick={()=>{ setDocFileList([]); docForm.resetFields(); }} disabled={!docFileList.length}>清空</Button>
            <Button onClick={()=>fetchDocuments()} loading={docLoading}>刷新列表</Button>
          </Form>
          <Table
            size="small"
            rowKey="id"
            loading={docLoading}
            dataSource={documents}
            pagination={{pageSize:8}}
            locale={{emptyText: <Empty description="暂无文档" />}}
            columns={[
              { title:'类型', dataIndex:'doc_type', key:'doc_type', render:v=> <Tag color="blue">{v}</Tag> },
              { title:'文件名', dataIndex:'file_name', key:'file_name', render:(v,r)=>(
                <button
                  type="button"
                  onClick={() => openWithAuth(r.url)}
                  style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1677ff' }}
                >
                  {v}
                </button>
              ) },
              { title:'到期', dataIndex:'expiry_date', key:'expiry_date', render:v=> v || '-' },
              { title:'上传时间', dataIndex:'uploaded_at', key:'uploaded_at', render:v=> v?.slice(0,19)||'-' },
              { title:'操作', key:'actions', render:(_,r)=> <Space>
                  <Button size="small" type="link" onClick={() => openWithAuth(r.url)}>查看</Button>
                  {canManage && <Button size="small" danger type="link" onClick={()=>handleDeleteDoc(r.id)}>删除</Button>}
                </Space> }
            ]}
          />
        </Space>
      </Modal>

      {/* 违章记录 */}
      <Modal
        title={detail ? `新增违章 - ${detail.license_plate}` : '新增违章'}
        open={violationModalOpen}
        onOk={handleCreateViolation}
        onCancel={() => setViolationModalOpen(false)}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form layout="vertical" form={violationForm} initialValues={{ severity:'中', occurred_at: new Date() }}>
          <Form.Item name="violation_type" label="违章类型" rules={[{ required: true }]}> 
            <Input placeholder="例如: 超速 / 未佩戴安全带" />
          </Form.Item>
          <Form.Item name="severity" label="严重程度" rules={[{ required: true }]}>
            <Select options={['低','中','高','严重'].map(v=>({value:v,label:v}))} />
          </Form.Item>
          <Form.Item name="occurred_at" label="发生时间" rules={[{ required: true }]}>
            <DatePicker showTime />
          </Form.Item>
          <Form.Item name="penalty_points" label="扣分">
            <InputNumber min={0} max={50} style={{ width:'100%' }} />
          </Form.Item>
          <Form.Item name="description" label="描述" rules={[{ required: true }]}> 
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 车况评估 */}
      <Modal
        title={detail ? `车况评估 - ${detail.license_plate}` : '车况评估'}
        open={evaluationModalOpen}
        onOk={handleCreateEvaluation}
        onCancel={() => setEvaluationModalOpen(false)}
        okText="提交"
        cancelText="取消"
        destroyOnClose
      >
        <Form layout="vertical" form={evaluationForm} initialValues={{ evaluate_date: new Date() }}>
          <Form.Item name="evaluate_date" label="评估日期" rules={[{ required: true }]}> 
            <DatePicker />
          </Form.Item>
          <Form.Item name="score" label="评分(0-100)" rules={[{ required: true }]}> 
            <InputNumber min={0} max={100} style={{ width:'100%' }} />
          </Form.Item>
          <Form.Item name="issues" label="存在问题"> 
            <Input.TextArea rows={3} />
          </Form.Item>
            <Form.Item name="recommendation" label="建议措施"> 
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 缺失合规员工列表 */}
      <Modal
        title="缺失车辆/证件的员工"
        open={missingModalOpen}
        onCancel={()=> setMissingModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Table
          rowKey="employee_id"
          loading={missingLoading}
          dataSource={missingList}
          pagination={{pageSize:10}}
          locale={{emptyText:<Empty description="全部员工已合规" />}}
          columns={[
            { title:'员工ID', dataIndex:'employee_id', key:'employee_id' },
            { title:'姓名', dataIndex:'employee_name', key:'employee_name' },
            { title:'车辆数', dataIndex:'vehicles_count', key:'vehicles_count' },
            { title:'驾驶证', dataIndex:'has_driver_license', key:'has_driver_license', render:v=> v? <Tag color="green">已上传</Tag> : <Tag color="red">缺失</Tag> },
            { title:'行驶证', dataIndex:'has_vehicle_registration', key:'has_vehicle_registration', render:v=> v? <Tag color="green">已上传</Tag> : <Tag color="red">缺失</Tag> },
            { title:'缺失项目', dataIndex:'missing_items', key:'missing_items', render: arr => Array.isArray(arr)? arr.map(i=> <Tag color="volcano" key={i}>{i}</Tag>) : '-' }
          ]}
        />
      </Modal>

    </div>
  );
};

export default EmployeeVehiclesPage;
